import React, { Component } from "react";
import { systemLogFilterMessages } from "../../helpers/messages";
import Select from "react-dropdown-select";
import DateRange from "../../shared/DateRange";
import { validateSearchFields, validateKeyword } from "./ValidateCaseFilters";
import { formatDate } from "../../helpers/GlobalFunctions";
import { SystemLogsRegions } from "../../helpers/constants";

export default class CaseSearchComponent extends Component {
	constructor(props) {
		super(props);
		this.state = {
			isSubmitted: false,
			filterObj: {
				searchString: '',
				region: '',
				searchBy: '',
				username: '',
				selectedUser: [],
				dateRange: [null, null],
				startDate: '',
				endDate: ''
			},
			errorMessage: '',
			today: new Date(),
			errors: {
				region: '',
				searchBy: '',
				searchString: '',
				startDate: '',
				endDate: '',
			},
		};
	}

	/**
	 * @description Handle input change
	 * @param  value Value of the input
	 * @param  field Input field name
	 * @memberof CaseSearchComponent
	 */
	handleOnChange = (field, value) => {
		var { errors } = this.state;
		var errorMessage = !value ? systemLogFilterMessages[field] : '';

		if (field === 'searchString') {
			if (value) {
				const searchStringRegex = validateKeyword(value);
				errorMessage = searchStringRegex.error;
			} else {
				errorMessage = '';
			}
		}

		if (field === "region") {
			this.setState(
				{ region: value, username: [], errors: { ...errors, region: "" } },
				() => {
					this.props.getUserList(value); // Fetch user list based on selected region
				}
			);
		}

		this.setState(prevState => {
			return {
				filterObj: {
					...prevState.filterObj,
					[field]: value
				},
				errors: {
					...errors,
					[field]: errorMessage
				}
			}
		});
	};

	/**
	 * @description Handle date range change
	 * @param  dateRange Selected date range array
	 * @memberof CaseSearchComponent
	 */
	handleDateChange = (dateRange) => {
		const [startDate, endDate] = dateRange;
		var { errors } = this.state;

		if (startDate && endDate) {
			if (startDate.getTime() > endDate.getTime()) {
				errors['endDate'] = systemLogFilterMessages.endDateBeforeStartDate;
			}
		}

		this.setState(prevState => {
			return {
				filterObj: {
					...prevState.filterObj,
					dateRange,
					startDate,
					endDate
				},
				errors: {
					...errors
				}
			}
		})
	}

	/**
	 * @description Handle dropdown selection for user
	 * @param  values Selected user array
	 * @memberof CaseSearchComponent
	 */
	handleUserSelection = (values) => {
		var stateObj = {}
		if (values?.length) {
			let selectedUser = values[0];
			let username = selectedUser.username;
			stateObj = {
				selectedUser,
				username
			}
		} else {
			stateObj = {
				selectedUser: [],
				username: '',
			}
		}

		this.setState(prevState => {
			return {
				filterObj: {
					...prevState.filterObj,
					...stateObj
				}
			}
		})
	};

	/**
	 * @description Function to validate fields at the time of submit
	 * @returns Boolean value if there are any errors
	 * @memberof CaseSearchComponent
	 */
	validateFields = () => {
		const { filterObj } = this.state;
		const searchFields = ['region', 'searchBy'];
		let { errorFlag, errors } = validateSearchFields({ editObj: filterObj, errors: this.state.errors }, searchFields);

		if (!errorFlag && filterObj.searchString) {
			let searchString = validateKeyword(filterObj.searchString);
			errorFlag = searchString.errorFlag;
			errors['searchString'] = searchString.error;
		}

		if (errorFlag) {
			this.setState(prevState => {
				return {
					errors: {
						...prevState.errors,
						...errors
					}
				}
			})
		}

		return errorFlag;
	}

	/**
	 * @description Validate inputs and trigger search
	 * @memberof CaseSearchComponent
	 */
	handleSubmit = () => {
		const { today, filterObj } = this.state;
		const { region, searchBy, searchString, startDate, endDate, username } = filterObj;
		const formHasErrors = this.validateFields();
		if (!formHasErrors) {
			var formData = { region, searchBy };
			var stateData = {};

			// If no search keyword AND dates are provided
			// Default daterange is set to most recent 15 days
			if (!searchString && (!startDate || !endDate)) {
				const defaultStartDate = new Date();
				defaultStartDate.setDate(today.getDate() - 14); // 15 days back, inclusive of both start and end date
				stateData = {
					filterObj: {
						...filterObj,
						dateRange: [defaultStartDate, today],
						startDate: defaultStartDate,
						endDate: today
					}
				}
				formData['startDate'] = formatDate(defaultStartDate);
				formData['endDate'] = formatDate(today);
			} 
			
			if (startDate && endDate) {
				formData['startDate'] = formatDate(startDate);
				formData['endDate'] = formatDate(endDate);
			}

			if (username) {
				formData['username'] = username;
			}

			if (searchString) {
				formData['searchString'] = searchString;
			}
			
			this.setState({ isSubmitted: true, ...stateData });
			this.props.handleSearchSubmit(formData);
		}
	};

	/**
	 * @description Handle enter key press to trigger search
	 * @memberof CaseSearchComponent
	 */
	onEnterPress = (e) => {
		if (e.which === 13) {
			e.preventDefault();
			this.handleSubmit();
		}
	};

	/**
	 * @description Reset all fields to initial state
	 * @memberof CaseSearchComponent
	 */
	handleReset = () => {
		this.setState({
			isSubmitted: false,
			filterObj: {
				region: '',
				searchBy: '',
				searchString: '',
				selectedUser: [],
				username: '',
				dateRange: [null, null],
				startDate: '',
				endDate: '',
			},
			errorMessage: '',
			errors: {
				region: '',
				searchBy: '',
				searchString: '',
				startDate: '',
				endDate: '',
			},
		});
		if (this.props.handleReset) {
			this.props.handleReset();
		}
	};

	render() {
		const { filterObj, errors, today } = this.state;
		const { searchString, region, searchBy, username } = filterObj;
		const { userList } = this.props;

		return (
			<>
				<div className="case-filters" data-testid="caseSearch">
					<div className="row">
						<div className="col-sm-12">
							<p className="fw-500 fs-9-rem"><em>Search by File Name for DAP uploads and Case ID for RI Insights uploads.</em></p>
						</div>
					</div>
					<div className="row">
						<div className="form-group col-sm-3">
							<div className="region-wrap">
								<div className="custom-label">
									<label>Region<span className="red">*</span></label>
								</div>
								<select
									className="form-select mr-4 case-search-dropdown"
									id="region-dropdown"
									data-testid="region-dropdown"
									value={region}
									onChange={(e) => this.handleOnChange("region", e.target.value)}
								>
									<option key="" value="">Select a region</option>
									{SystemLogsRegions.map(x => <option key={x.value} value={x.value}>{x.label}</option>)}
								</select>
							</div>
							{errors.region && (
								<div className="customer-error-msg">
									<p>{errors.region}</p>
								</div>
							)}
						</div>
						<div className="form-group col-sm-3">
							<div className="search-type-wrap">
								<div className="custom-label">
									<label htmlFor="search-type-dropdown">Search By<span className="red">*</span></label>
								</div>
								<select
									className="form-select mr-4 case-search-dropdown"
									id="search-type-dropdown"
									data-testid="search-type-dropdown"
									value={searchBy}
									onChange={(e) => this.handleOnChange("searchBy", e.target.value)}
								>
									<option key="" value="">Select search option</option>
									<option key="caseId" value="caseId">Case ID (RI Insights Uploads)</option>
									<option key="filename" value="zip">Uploaded ZIP File (DAP Uploads)</option>
								</select>
							</div>
							{errors.searchBy && (
								<div className="customer-error-msg">
									<p>{errors.searchBy}</p>
								</div>
							)}
						</div>
						<div className="form-group col-sm-3">
							<div className="date-wrap">
								<div className="custom-label">
									<label htmlFor="upload-datepicker">Upload Date Range</label>
									<span className="ms-1 cursor-pointer" title="Last 15 days selected by default.">
										<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" className="bi bi-info-circle-fill" viewBox="0 0 16 16">
											<path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" />
										</svg>
									</span>
								</div>
								<div className="daterange-wrap">
									<DateRange
										className="form-control case-search-input"
										id="upload-datepicker"
										placeholderText="Select date range"
										dateFormat="dd/MM/yyyy"
										maxDate={today}
										onChange={this.handleDateChange}
										startDate={filterObj.startDate}
										endDate={filterObj.endDate}
									/>
								</div>
							</div>
							{errors.startDate && (
								<div className="customer-error-msg">
									<p>{errors.startDate}</p>
								</div>
							)}
							{errors.endDate && (
								<div className="customer-error-msg">
									<p>{errors.endDate}</p>
								</div>
							)}
						</div>
						<div className="form-group col-sm-3">
							<div className="custom-label">
								<label htmlFor="case-search">Keywords</label>
							</div>
							<input
								type="text"
								className="form-control case-search-input"
								id="case-search"
								placeholder="Search for cases"
								data-testid="case-search"
								value={searchString}
								onChange={(e) => this.handleOnChange("searchString", e.target.value)}
								onKeyUp={this.onEnterPress}
							/>
							{errors.searchString && (
								<div className="customer-error-msg">
									<p>{errors.searchString}</p>
								</div>
							)}
						</div>
					</div>
					{region && (
						<div className="row">
							<div className="form-group col-sm-3">
								<div className="user-wrap">
									<div className="custom-label">
										<label htmlFor="select-user">Uploaded By</label>
									</div>
									<div className="case-filter-searchable-dropdown">
										<Select
											id="select-user"
											data-testid="select-user"
											className="searchable-user-select"
											placeholder="Search by username, email, name"
											disabled={false}
											options={userList}
											clearable={false}
											searchBy="searchByField"
											dropdownHandle={true}
											values={username ? username : []}
											labelField="fullName"
											valueField="username"
											onChange={this.handleUserSelection}
										/>
									</div>
								</div>
							</div>
						</div>
					)}

					<div className="row">
						<div className="form-group col-sm-3">
							<div className="case-search-btn-wrap">
								<button type="button" className="btn btn-primary me-2" id="search-submit" data-testid="search-submit" onClick={this.handleSubmit}>Search</button>
								<button type="button" className="btn btn-secondary" id="search-reset" data-testid="search-reset" onClick={this.handleReset}>Reset</button>
							</div>
						</div>
					</div>
				</div>
			</>
		);
	}
}
